import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Button from "../../atoms/Button";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import { ReactComponent as CheckCircleIcon } from "../../../images/check-circle-icon.svg";
import {
    formatAccountDeletionDeadline,
    formatAccountDeletionStatusCheckedAt,
} from "./formatDeadline";

const RECOVERABLE_STATUSES = new Set(["RECOVERY_WINDOW"]);

/* Every backend status maps to one public sentence and one visual tone. The tone
   is what the card reads as at a glance — a spinner while the platform is still
   working, a check once the deletion is off, muted once nothing can be done. */
const PUBLIC_STATUS_PRESENTATION = Object.freeze({
    DEACTIVATING: { key: "accountDeletionPublic.recovery.statuses.deactivating", tone: "progress" },
    RECOVERY_WINDOW: { key: "accountDeletionPublic.recovery.statuses.recoverable", tone: "ready" },
    CANCELLATION_IN_PROGRESS: { key: "accountDeletionPublic.recovery.statuses.restoring", tone: "progress" },
    CANCELLED: { key: "accountDeletionPublic.recovery.statuses.cancelled", tone: "success" },
    ERASURE_READY: { key: "accountDeletionPublic.recovery.statuses.unavailable", tone: "closed" },
    ERASURE_IN_PROGRESS: { key: "accountDeletionPublic.recovery.statuses.unavailable", tone: "closed" },
    HELD: { key: "accountDeletionPublic.recovery.statuses.unavailable", tone: "closed" },
    ERASURE_PAUSED_HELD: { key: "accountDeletionPublic.recovery.statuses.unavailable", tone: "closed" },
    COMPLETED: { key: "accountDeletionPublic.recovery.statuses.completed", tone: "closed" },
});

const UNKNOWN_PRESENTATION = Object.freeze({
    key: "accountDeletionPublic.recovery.statuses.unknown",
    tone: "closed",
});

/* Sized against the 40px indicator slot: large enough to register as motion,
   small enough that the pulsing dots never crowd the status headline. */
const STATUS_SPINNER_SIZE = 24;

export const isKnownRecoveryStatus = (value) => Boolean(PUBLIC_STATUS_PRESENTATION[value]);

/* The panel already announces the status in words, so the indicator is decorative
   and stays out of the accessibility tree — otherwise the spinner's own live
   region would announce "Loading" on top of the sentence beside it. */
const StatusIndicator = ({ tone }) => (
    <span className="account-deletion-public-card__status-indicator" aria-hidden="true">
        {tone === "progress" && <LoadingSpinner size={STATUS_SPINNER_SIZE} />}
        {tone === "success" && (
            <CheckCircleIcon className="account-deletion-public-card__status-icon" focusable="false" />
        )}
        {tone !== "progress" && tone !== "success" && (
            <span className="account-deletion-public-card__status-dot" />
        )}
    </span>
);

StatusIndicator.propTypes = {
    tone: PropTypes.string.isRequired,
};

const RecoveryStatusPanel = ({
    status,
    hasConfirmation,
    isSubmitting,
    onConfirm,
    onRefresh,
    lastCheckedAt,
    panelRef,
}) => {
    const { t, i18n } = useTranslation();
    const loginAllowed = status?.status === "CANCELLED" && status?.loginAllowed === true;
    const attentionRequired = status?.attentionRequired === true;
    const canAttemptRecovery = RECOVERABLE_STATUSES.has(status?.status) && hasConfirmation;
    const presentation = PUBLIC_STATUS_PRESENTATION[status?.status] || UNKNOWN_PRESENTATION;
    /* A blocked safety review outranks the underlying status: the card must read as
       "waiting on us" rather than as quiet progress. */
    const tone = attentionRequired ? "attention" : presentation.tone;
    const recoveryDeadline = RECOVERABLE_STATUSES.has(status?.status)
        ? formatAccountDeletionDeadline(status?.recoverableUntil, i18n?.language)
        : null;
    const statusCheckedAt = formatAccountDeletionStatusCheckedAt(lastCheckedAt, i18n?.language);

    return (
        <div
            ref={panelRef}
            className={`account-deletion-public-card__status account-deletion-public-card__status--${tone}`}
            role="status"
            aria-live="polite"
            tabIndex={-1}
        >
            <div className="account-deletion-public-card__status-head">
                <StatusIndicator tone={tone} />
                <div className="account-deletion-public-card__status-headline">
                    <p className="account-deletion-public-card__status-label">
                        {t("accountDeletionPublic.recovery.statusLabel")}
                    </p>
                    <p className="account-deletion-public-card__status-title">
                        {t(presentation.key)}
                    </p>
                </div>
            </div>

            {(recoveryDeadline || attentionRequired || statusCheckedAt) && (
                <div className="account-deletion-public-card__status-details">
                    {recoveryDeadline && (
                        <p>{t("accountDeletionPublic.recovery.recoverableUntil", {
                            deadline: recoveryDeadline,
                        })}</p>
                    )}
                    {attentionRequired && (
                        <p className="account-deletion-public-card__status-warning">
                            {t("accountDeletionPublic.recovery.attentionRequired")}
                        </p>
                    )}
                    {statusCheckedAt && (
                        <p className="account-deletion-public-card__meta">
                            {t("accountDeletionPublic.recovery.lastChecked", {
                                time: statusCheckedAt,
                            })}
                        </p>
                    )}
                </div>
            )}

            <div className="account-deletion-public-card__status-actions">
                {!loginAllowed && canAttemptRecovery && (
                    <Button variant="danger" onClick={onConfirm} disabled={isSubmitting}>
                        {isSubmitting
                            ? t("accountDeletionPublic.recovery.actions.restoring")
                            : t("accountDeletionPublic.recovery.actions.restore")}
                    </Button>
                )}
                {!loginAllowed && (
                    <Button variant="secondary" onClick={onRefresh} disabled={isSubmitting}>
                        {t("accountDeletionPublic.recovery.actions.refresh")}
                    </Button>
                )}
                {loginAllowed && (
                    <a className="account-deletion-public-card__status-cta" href="/login">
                        {t("accountDeletionPublic.recovery.actions.login")}
                    </a>
                )}
            </div>
        </div>
    );
};

RecoveryStatusPanel.propTypes = {
    status: PropTypes.shape({
        status: PropTypes.string,
        recoverableUntil: PropTypes.string,
        loginAllowed: PropTypes.bool,
        attentionRequired: PropTypes.bool,
    }),
    hasConfirmation: PropTypes.bool.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    onConfirm: PropTypes.func.isRequired,
    onRefresh: PropTypes.func.isRequired,
    lastCheckedAt: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.instanceOf(Date),
    ]),
    panelRef: PropTypes.shape({ current: PropTypes.any }),
};

export default RecoveryStatusPanel;
